import { defineStore } from "pinia";
import { type Ref, ref, computed } from "vue";

import apiSkobo from "@/helpers/api/apiSkobo";
import apiGraph from "@/helpers/api/apiGraph";
import GraphHelper from "@/helpers/utilities/GraphHelper";
import FileHelper from "@/helpers/utilities/FileHelper";
import TrackHelper from "@/helpers/utilities/TrackHelper";
import { i18n } from "@/helpers/utilities/I18nHelper";

import Section from "@/models/Section/Section";
import DriveItem from "@/models/Documents/DriveItem";
import SectionExtension from "@/models/extensions/education/SectionExtension";

import { useSettingsStore } from "@/stores/settings";

// ---------- local types ----------
interface SectionMember {
  userId: string;
  extSysApiUserId?: string;
  emailSchoolAddress?: string;
  name: { name: string; firstName: string; lastName: string; title?: string | null };
  roles?: string[];
}

interface SectionWithMembers {
  sectionId: string;
  name: string;
  institutionId?: string;
  extSysApiSectionId?: string;
  hasConfidentialFoldersForUser?: boolean;
  archived?: boolean;
  teachers: SectionMember[];
  students: SectionMember[];
  [key: string]: unknown;
}

interface ArchivedSectionGroup {
  title: string;
  sections: Record<string, SectionWithMembers>;
}

interface SectionDriveSet {
  sharedFilesDrive?: DriveItem;
  classMaterialsDrive?: DriveItem;
  studentWorkDrive?: DriveItem;
  assignmentDrive?: DriveItem;
  assignmentFilesDrive?: DriveItem;
  studentWorkDriveData?: DriveItem;
  [key: string]: DriveItem | undefined;
}

interface RawDrive {
  id: string;
  name: string;
  driveType: string;
  createdBy?: { user?: { displayName?: string } };
  lastModifiedDateTime?: string;
  webUrl?: string;
  parentReference?: { driveId?: string; id?: string };
}

interface SectionDriveDescriptor {
  driveId: string;
  name: string;
  additionalData: { sectionId: string };
  webUrl?: string;
}

interface BatchRequest {
  id: string;
  method: string;
  url: string;
}

interface BatchResponseEntry {
  id: string;
  status: number;
  headers?: Record<string, string>;
  body?: unknown;
}

interface PeopleTableEntry {
  name: { name: string; firstName: string; lastName: string };
  userId: string;
  sectionId: string;
  sectionName: string;
}

export const useSectionsStore = defineStore("section", () => {
  // ---------- state ----------
  // sections
  const mySectionsMap: Ref<Record<string, SectionWithMembers> | null> = ref(null);
  const myArchivedSectionGroupsMap: Ref<ArchivedSectionGroup[] | Record<string, never>> = ref({});
  const allSectionsOnInstitutionState: Ref<unknown[] | null> = ref(null);

  // drives
  const sectionRootDriveMap: Ref<Record<string, SectionDriveSet>> = ref({});

  // members
  const sectionsTeachersMap: Ref<Record<string, SectionMember[]>> = ref({});
  const sectionsStudentsMap: Ref<Record<string, SectionMember[]>> = ref({});
  const sectionsProfilePictures: Ref<Record<string, string | null>> = ref({});

  // system drives
  const sectionRootSystemDriveMap: Ref<Record<string, RawDrive>> = ref({});
  const sectionRootSubDrivesMap: Ref<Record<string, RawDrive[]>> = ref({});

  // ---------- sections getters ----------
  const mySections = computed(() => mySectionsMap.value);
  const myArchivedSections = computed(() => myArchivedSectionGroupsMap.value);

  const groupingTitleForArchivedSection = computed(() => (sectionId: string) => {
    const groups = myArchivedSectionGroupsMap.value as ArchivedSectionGroup[];
    for (let i = 0; i < groups.length; i++) {
      if (Object.keys(groups[i].sections).indexOf(sectionId) > -1) {
        return groups[i].title;
      }
    }
  });

  const section = computed(() => (sectionId: string) => (mySectionsMap.value ? mySectionsMap.value[sectionId] : null));
  const allSectionsOnInstitution = computed(() => allSectionsOnInstitutionState.value);
  const peopleInMySections = computed(() =>
    mySectionsMap.value ? Object.values(mySectionsMap.value).flatMap(s => [...s.students, ...s.teachers]) : null
  );

  const mySectionsFromSearch = computed(() => (searchTerm: string) => {
    const term = searchTerm.toLowerCase();
    const sectionsArray: SectionWithMembers[] = mySectionsMap.value ? Object.values(mySectionsMap.value) : [];
    return sectionsArray.filter(p => p.name.toLowerCase().includes(term));
  });

  const allStudentsAsPeopleTableObjectsFromSectionsSearched = computed(() => (searchTerm: string) => {
    const filteredSections = mySectionsFromSearch.value(searchTerm);

    return filteredSections.flatMap((s): PeopleTableEntry[] => {
      return s.students.map(u => ({
        name: { name: u.name.name, firstName: u.name.firstName, lastName: u.name.lastName },
        userId: u.userId,
        sectionId: s.sectionId,
        sectionName: s.name
      }));
    });
  });

  const allMyStudentsSearchedAsPeopleTableObejcts = computed(() => (searchTerm: string) => {
    const sectionsArray: SectionWithMembers[] = mySectionsMap.value ? Object.values(mySectionsMap.value) : [];
    return sectionsArray.flatMap((s): PeopleTableEntry[] => {
      return s.students
        .filter(u => u.name.name.toLowerCase().includes(searchTerm))
        .map(u => ({
          name: { name: u.name.name, firstName: u.name.firstName, lastName: u.name.lastName },
          userId: u.userId,
          sectionId: s.sectionId,
          sectionName: s.name
        }));
    });
  });

  const sectionIdForPersonId = computed(() => (personId: string) => {
    let result: SectionWithMembers | undefined;

    if (!mySectionsMap.value) {
      return;
    }

    Object.keys(mySectionsMap.value).forEach(secId => {
      if (mySectionsMap.value![secId].students.find(st => st.userId === personId)) {
        result = mySectionsMap.value![secId];
        return result;
      }
    });
  });

  // ---------- members getters ----------
  const archivedSectionsFlat = computed(() => {
    const archived = myArchivedSectionGroupsMap.value as ArchivedSectionGroup[];
    if (!archived || Object.keys(archived).length === 0) {
      return {};
    }
    return Object.fromEntries(
      Object.values(archived)
        .flatMap(g => g.sections)
        .flatMap(t => Object.values(t))
        .map((i: SectionWithMembers) => [i.sectionId, i])
    );
  });

  const sectionWithArchivedFallback = computed(() => (sectionId: string): SectionWithMembers | null => {
    const sec = mySectionsMap.value?.[sectionId];
    if (sec) {
      return sec;
    }
    const archived = myArchivedSectionGroupsMap.value as ArchivedSectionGroup[];
    if (!archived || Object.keys(archived).length === 0) {
      return null;
    }
    const flat: SectionWithMembers[] = Object.values(archived).flatMap(group => Object.values(group.sections));
    return flat.find(s => s.sectionId === sectionId) || null;
  });

  const allMyTeachers = computed(() =>
    mySectionsMap.value ? Object.values(mySectionsMap.value).flatMap(sec => sec.teachers || []) : []
  );

  const allMyStudents = computed(() =>
    mySectionsMap.value ? Object.values(mySectionsMap.value).flatMap(sec => sec.students || []) : []
  );

  const sectionMember = computed(() => (sectionId: string, extSysApiUserId: string) => {
    if (mySectionsMap.value?.[sectionId]) {
      const teachers = mySectionsMap.value[sectionId].teachers || [];
      const students = mySectionsMap.value[sectionId].students || [];
      return teachers.concat(students).find(p => p.extSysApiUserId === extSysApiUserId);
    }

    const archived = myArchivedSectionGroupsMap.value as ArchivedSectionGroup[];
    if (archived) {
      const archivedSection = Object.values(archived)
        .flatMap(s => Object.values(s.sections || {}))
        .find((s: SectionWithMembers) => s.sectionId === sectionId);
      if (archivedSection) {
        const teachers = archivedSection.teachers || [];
        const students = archivedSection.students || [];
        return teachers.concat(students).find(p => p.extSysApiUserId === extSysApiUserId);
      }
    }
    return null;
  });

  const sectionMembers = computed(() => (sectionId: string) => {
    const teachers = sectionsTeachersMap.value[sectionId] || [];
    const students = sectionsStudentsMap.value[sectionId] || [];
    return teachers.concat(students);
  });

  const sectionTeachers = computed(() => (sectionId: string) => sectionsTeachersMap.value[sectionId] || []);
  const sectionStudents = computed(() => (sectionId: string) => sectionsStudentsMap.value[sectionId] || []);

  // ---------- drive getters ----------
  const sharedFilesDrive = computed(
    () => (sectionId: string) => sectionRootDriveMap.value[sectionId]?.sharedFilesDrive
  );
  const classMaterialsDrive = computed(
    () => (sectionId: string) => sectionRootDriveMap.value[sectionId]?.classMaterialsDrive
  );
  const studentWorkDrive = computed(() => (sectionId: string) => sectionRootDriveMap.value[sectionId]?.studentWork);
  const assignmentDrive = computed(() => (sectionId: string) => sectionRootDriveMap.value[sectionId]?.assignmentDrive);
  const sectionDriveType = computed(
    () => (driveId: string) =>
      Object.values(sectionRootDriveMap.value)
        .flatMap(sec => Object.values(sec))
        .find((drive): drive is DriveItem => !!drive && drive.parentReference?.driveId === driveId)
  );

  // ---------- system drives getters ----------
  const allSectionsDrives = computed(() => {
    const driveObjects: SectionDriveDescriptor[] = [];
    Object.keys(sectionRootSystemDriveMap.value).forEach(key => {
      if (mySectionsMap.value?.[key] && sectionRootSystemDriveMap.value[key]) {
        driveObjects.push({
          driveId: sectionRootSystemDriveMap.value[key].id,
          name: mySectionsMap.value[key].name,
          additionalData: { sectionId: key },
          webUrl: sectionRootSystemDriveMap.value[key].webUrl
        });
      }
    });
    return driveObjects;
  });

  const allArchivedSectionsDrives = computed(() => {
    const driveObjects: SectionDriveDescriptor[] = [];
    const archived = myArchivedSectionGroupsMap.value as ArchivedSectionGroup[];
    if (!archived || Object.keys(archived).length === 0) {
      return driveObjects;
    }

    const sectionLookup: Record<string, { section: SectionWithMembers; groupTitle: string }> = {};
    Object.values(archived).forEach(group => {
      Object.entries(group.sections || {}).forEach(([sectionId, sec]) => {
        sectionLookup[sectionId] = { section: sec as SectionWithMembers, groupTitle: group.title };
      });
    });

    Object.keys(sectionRootSystemDriveMap.value).forEach(key => {
      const lookup = sectionLookup[key];
      const drive = sectionRootSystemDriveMap.value[key];
      if (lookup && drive) {
        driveObjects.push({
          driveId: drive.id,
          name: lookup.section.name + " (" + lookup.groupTitle + ")",
          additionalData: { sectionId: key }
        });
      }
    });
    return driveObjects;
  });

  const sectionSubDrives = computed(() => (driveId: string) => sectionRootSubDrivesMap.value[driveId]);

  const parentOfSubDrive = computed(() => (subDriveId: string) => {
    try {
      for (const i in sectionRootSubDrivesMap.value) {
        if (sectionRootSubDrivesMap.value[i].some(s => s.parentReference?.driveId === subDriveId)) {
          return i;
        }
      }
      // eslint-disable-next-line no-empty
    } catch {}
    return null;
  });

  const sectionFromDriveId = computed(() => (driveId: string) => {
    const sectionRootFolder = allSectionsDrives.value.find(asd => asd.driveId === driveId);
    if (sectionRootFolder) {
      return sectionWithArchivedFallback.value(sectionRootFolder.additionalData.sectionId);
    }

    const subSectionDriveId = parentOfSubDrive.value(driveId);
    if (subSectionDriveId) {
      const parentDrive = allSectionsDrives.value.find(asd => asd.driveId === subSectionDriveId);
      if (parentDrive) {
        return sectionWithArchivedFallback.value(parentDrive.additionalData.sectionId);
      }
    }

    return null;
  });

  // ---------- sections actions ----------
  /**
   * Reload sections no matter if the are in state or not
   */
  const reloadMySections = async (sectionId?: string) => {
    const response = await apiSkobo.get("/me/section/?options=teachers,students,teams");

    const sections: Record<string, SectionWithMembers> = {};
    response.data.forEach((sect: unknown) => {
      const sectionInstance = new Section(sect) as SectionWithMembers;
      sections[sectionInstance.sectionId] = sectionInstance;
    });

    mySectionsMap.value = sections;

    if (sectionId && !sections[sectionId]) {
      loadMyArchivedSections();
    }
  };

  /**
   * Load sections for current user if they are not in state
   */
  const loadMySections = async (sectionId?: string) => {
    if (!mySectionsMap.value || (sectionId && !mySectionsMap.value[sectionId])) {
      await reloadMySections(sectionId);
    }
  };

  /**
   * Reload archived sections no matter if the are in state or not
   */
  const reloadMyArchivedSections = async () => {
    const response = await apiSkobo.get("/v2/me/section/archived/teachers,students,teams");

    const resultingSectionGroups: ArchivedSectionGroup[] = [];

    interface ArchivedSectionRaw {
      sectionId: string;
      group: string;
      [key: string]: unknown;
    }

    const groups: Record<string, ArchivedSectionRaw[]> = response.data.reduce(
      (acc: Record<string, ArchivedSectionRaw[]>, curr: ArchivedSectionRaw) => {
        if (!acc[curr.group]) acc[curr.group] = [];
        acc[curr.group].push(curr);
        return acc;
      },
      {}
    );

    for (const key in groups) {
      let title = key.replace("Year roll", "").trim();
      const regex = /(2\d{1})/gm;
      title = title.replaceAll(regex, "20$1");

      const sections: Record<string, SectionWithMembers> = {};

      groups[key].forEach(sect => {
        sections[sect.sectionId] = new Section(sect) as SectionWithMembers;
      });

      resultingSectionGroups.push({ title, sections });
    }

    const settingsStore = useSettingsStore();
    resultingSectionGroups.sort((a, b) => b.title.localeCompare(a.title, settingsStore.language));
    myArchivedSectionGroupsMap.value = resultingSectionGroups;
  };

  /**
   * Load archived sections for current user if they are not in state
   */
  const loadMyArchivedSections = async () => {
    const archived = myArchivedSectionGroupsMap.value as ArchivedSectionGroup[];
    if (archived.length > 0) {
      return;
    }
    await reloadMyArchivedSections();
  };

  const loadAllSectionsOnInstitution = async () => {
    if (!allSectionsOnInstitutionState.value) {
      const response = await apiSkobo.get("/Institution/Section");
      allSectionsOnInstitutionState.value = response.data;
    }
  };

  // ---------- members actions ----------
  const addSectionToMySections = async (sect: SectionWithMembers) => {
    if (mySectionsMap.value) {
      mySectionsMap.value[sect.sectionId] = sect;
    }
    await apiSkobo.put("/me/Section/" + sect.sectionId);
    await reloadMySections();
  };

  const removeSectionFromMySections = async (sectionId: string) => {
    if (mySectionsMap.value) {
      delete mySectionsMap.value[sectionId];
    }
    await apiSkobo.delete("/me/Section/" + sectionId);
    await reloadMySections();
  };

  const loadMembersForSection = async (sectionId: string) => {
    if (sectionsStudentsMap.value[sectionId] && sectionsTeachersMap.value[sectionId]) {
      return;
    }

    const response = await apiSkobo.get(`/Institution/Section/${sectionId}/Members`);
    const settingsStore = useSettingsStore();

    if (response.data.students && response.data.students.values) {
      // Trigger profile picture load on people store via global dispatch is not available here.
      // Caller can also trigger people store loadProfilePicturesFromList if needed.
    }

    if (response.data.teachers?.teachers) {
      sectionsTeachersMap.value[sectionId] = response.data.teachers.teachers.sort(
        (a: SectionMember, b: SectionMember) => a.name.name.localeCompare(b.name.name, settingsStore.language)
      );
    }

    if (response.data.students?.students) {
      sectionsStudentsMap.value[sectionId] = response.data.students.students.sort(
        (a: SectionMember, b: SectionMember) => a.name.name.localeCompare(b.name.name, settingsStore.language)
      );
    }
  };

  const sectionsDoLoadProfilePicture = (extSysApiSectionId: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      apiGraph
        .get(`/groups/${extSysApiSectionId}/photos/48x48/$value`, { responseType: "blob" })
        .then((response: { data: BlobPart }) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          sectionsProfilePictures.value[extSysApiSectionId] = url;
          resolve(url);
        })
        .catch(() => reject());
    });
  };

  const sectionsLoadProfilePicturesFromList = async (sectionList: SectionWithMembers[]) => {
    const requestForSectionsNotInState = sectionList
      .filter(s => s.extSysApiSectionId && !sectionsProfilePictures.value[s.extSysApiSectionId])
      .map(s => {
        sectionsProfilePictures.value[s.extSysApiSectionId] = null;
        return SectionExtension.generateGraphBatchRequestFromSection(s);
      });

    if (requestForSectionsNotInState.length === 0) {
      return;
    }

    try {
      const results: BatchResponseEntry[] = await GraphHelper.makeBatchRequest(requestForSectionsNotInState);
      results.map(u => {
        if (u.status === 200) {
          const url = "data:" + u.headers!["Content-Type"] + ";base64," + u.body;
          fetch(url).then(res =>
            res.blob().then(blob => {
              const objectUrl = window.URL.createObjectURL(blob);
              sectionsProfilePictures.value[u.id] = objectUrl;
            })
          );
        }
      });
    } catch (error: unknown) {
      sectionList.map(s => {
        delete sectionsProfilePictures.value[s.extSysApiSectionId];
      });
      throw (error as Error).message;
    }
  };

  const sectionsLoadProfilePicture = (extSysApiSectionId: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!extSysApiSectionId) {
        reject();
        return;
      }

      if (Object.prototype.hasOwnProperty.call(sectionsProfilePictures.value, extSysApiSectionId)) {
        if (sectionsProfilePictures.value[extSysApiSectionId] !== null) {
          resolve(sectionsProfilePictures.value[extSysApiSectionId] as string);
          return;
        }

        let i = 0;
        const intervaller = setInterval(() => {
          if (sectionsProfilePictures.value[extSysApiSectionId] !== null) {
            clearInterval(intervaller);
            resolve(sectionsProfilePictures.value[extSysApiSectionId] as string);
            return;
          }
          i++;
          if (i > 5) {
            clearInterval(intervaller);
            return sectionsDoLoadProfilePicture(extSysApiSectionId).then(response => resolve(response));
          }
        }, 1000);
      } else {
        return sectionsDoLoadProfilePicture(extSysApiSectionId).then(response => resolve(response));
      }
    });
  };

  const loadSectionWithFallback = async (sectionId: string) => {
    if (!mySectionsMap.value || !mySectionsMap.value[sectionId]) {
      await reloadMySections();
    }
    return mySectionsMap.value?.[sectionId];
  };

  const loadArchivedSectionById = async (sectionId: string) => {
    await loadMyArchivedSections();
    const archived = myArchivedSectionGroupsMap.value as ArchivedSectionGroup[];
    const archivedSectionAfterLoad = [...Object.values(archived).flatMap(g => Object.values(g.sections))].find(
      (s: SectionWithMembers) => s.sectionId === sectionId
    );
    return archivedSectionAfterLoad;
  };

  // ---------- drive actions ----------
  /**
   * Load drives for a section
   */
  const reloadDrives = async (sectionId: string) => {
    try {
      if (!section.value(sectionId)?.extSysApiSectionId) {
        return;
      }

      const response = await apiGraph.get(
        `/groups/${section.value(sectionId)!.extSysApiSectionId}/drives?$select=id,name,createdBy,driveType,lastModifiedDateTime,webUrl`
      );

      const sectionDrivesObject: SectionDriveSet = {};

      const rootFolder = response?.data?.value?.find(
        (d: RawDrive) => d.driveType === "documentLibrary" && d.createdBy?.user?.displayName?.startsWith("System")
      );

      if (rootFolder) {
        const rootFolderChildrenResponse = await apiGraph.get(`/drives/${rootFolder.id}/root/children`);

        const sharedFilesDriveData = rootFolderChildrenResponse.data.value.find(
          (f: { name: string }) => f.name === "General" || f.name === "Generel"
        );

        if (sharedFilesDriveData) {
          sectionDrivesObject.sharedFilesDrive = new DriveItem({
            name: i18n.global.te("DriveItem." + sharedFilesDriveData.name)
              ? i18n.global.t("DriveItem." + sharedFilesDriveData.name)
              : sharedFilesDriveData.name,
            id: sharedFilesDriveData.id,
            createdBy: sharedFilesDriveData.createdBy,
            lastModifiedDateTime: sharedFilesDriveData.lastModifiedDateTime,
            extension: FileHelper.getClosedFolderTemplate(),
            folder: true,
            parentReference: {
              driveId: sharedFilesDriveData.parentReference.driveId,
              id: sharedFilesDriveData.parentReference.id
            },
            webUrl: sharedFilesDriveData.webUrl
          });
        }
      }

      const classMaterialsDriveData = response?.data?.value?.find(
        (d: RawDrive) => d.driveType === "documentLibrary" && d.name?.toLowerCase().includes("material")
      );

      if (classMaterialsDriveData) {
        sectionDrivesObject.classMaterialsDrive = new DriveItem({
          name: i18n.global.te("DriveItem." + classMaterialsDriveData.name)
            ? i18n.global.t("DriveItem." + classMaterialsDriveData.name)
            : classMaterialsDriveData.name,
          createdBy: classMaterialsDriveData.createdBy,
          lastModifiedDateTime: classMaterialsDriveData.lastModifiedDateTime,
          extension: FileHelper.getClosedFolderTemplate(),
          folder: true,
          parentReference: {
            driveId: classMaterialsDriveData.id
          },
          webUrl: classMaterialsDriveData.webUrl
        });
      }

      const assignmentDriveData = response?.data?.value?.find(
        (d: RawDrive) =>
          d.driveType === "documentLibrary" &&
          d.createdBy?.user?.displayName?.toLowerCase().startsWith("SharePoint") &&
          d.name?.toLowerCase().includes("File")
      );

      if (assignmentDriveData) {
        sectionDrivesObject.assignmentFilesDrive = new DriveItem({
          name: i18n.global.te("DriveItem." + assignmentDriveData.name)
            ? i18n.global.t("DriveItem." + assignmentDriveData.name)
            : assignmentDriveData.name,
          createdBy: assignmentDriveData.createdBy,
          lastModifiedDateTime: assignmentDriveData.lastModifiedDateTime,
          extension: FileHelper.getClosedFolderTemplate(),
          folder: true,
          parentReference: {
            driveId: assignmentDriveData.id
          },
          webUrl: assignmentDriveData.webUrl
        });
      }

      const studentWorkDriveData = response?.data?.value?.find(
        (d: RawDrive) =>
          d.driveType === "documentLibrary" &&
          d.createdBy?.user?.displayName?.toLowerCase().startsWith("SharePoint") &&
          d.name?.toLowerCase().startsWith("Student")
      );

      if (studentWorkDriveData) {
        sectionDrivesObject.studentWorkDrive = new DriveItem({
          name: i18n.global.te("DriveItem." + studentWorkDriveData.name)
            ? i18n.global.t("DriveItem." + studentWorkDriveData.name)
            : studentWorkDriveData.name,
          createdBy: studentWorkDriveData.createdBy,
          lastModifiedDateTime: studentWorkDriveData.lastModifiedDateTime,
          extension: FileHelper.getClosedFolderTemplate(),
          folder: true,
          parentReference: {
            driveId: studentWorkDriveData.id
          },
          webUrl: studentWorkDriveData.webUrl
        });
      }

      // Lookup folder ids on drives missing them
      const batchRequests: BatchRequest[] = [];
      for (const driveKey in sectionDrivesObject) {
        const drive = sectionDrivesObject[driveKey];
        if (drive && !drive.parentReference.id) {
          batchRequests.push({
            id: driveKey,
            method: "GET",
            url: `/drives/${drive.parentReference.driveId}/root`
          });
        }
      }

      if (batchRequests.length > 0) {
        const batchResponse = await apiGraph.post("/$batch", { requests: batchRequests });

        for (const resp of batchResponse.data.responses as BatchResponseEntry[]) {
          const driveKey = resp.id;
          const driveItem = resp.status === 200 ? resp.body : null;
          if (!driveItem) {
            continue;
          } else {
            sectionDrivesObject[driveKey] = new DriveItem(resp.body);
          }
        }
      }

      sectionRootDriveMap.value[sectionId] = sectionDrivesObject;
    } catch (error) {
      TrackHelper.registerError(error, "Failed to load drives for section " + sectionId);
    }
  };

  const loadDrives = async (sectionId: string) => {
    if (!sectionRootDriveMap.value[sectionId]) {
      await reloadDrives(sectionId);
    }
  };

  const findFilesTabOnGeneralTeamsChannel = async (sectionId: string) => {
    try {
      const sec = section.value(sectionId);

      if (!sec?.extSysApiSectionId) {
        return null;
      }

      const channelsResponse = await apiGraph.get(`/teams/${sec.extSysApiSectionId}/channels?$select=id,displayName`);
      const generalChannel = channelsResponse.data.value.find(
        (c: { displayName: string }) => c.displayName === "General"
      );

      if (!generalChannel) {
        return null;
      }

      const tabsResponse = await apiGraph.get(
        `/teams/${sec.extSysApiSectionId}/channels/${generalChannel.id}/tabs?$select=id,displayName,webUrl`
      );

      const filesTab = tabsResponse.data.value.find((t: { displayName: string }) => t.displayName === "Files");

      return filesTab || null;
    } catch {
      return null;
    }
  };

  // ---------- system drives actions ----------
  const loadSystemDrivesForSection = async ({
    sectionId,
    forceReload = false
  }: {
    sectionId: string;
    forceReload?: boolean;
  }) => {
    if (!forceReload && sectionRootSystemDriveMap.value[sectionId]) {
      return;
    }

    try {
      const sec = sectionWithArchivedFallback.value(sectionId);

      if (!sec || !sec.extSysApiSectionId) {
        return;
      }

      const response = await apiGraph.get(
        `/groups/${sec.extSysApiSectionId}/drives?$select=id,name,createdBy,driveType,lastModifiedDateTime,webUrl`
      );

      const filteredDrives: RawDrive[] = response.data.value.filter(
        (d: RawDrive) => d.driveType === "documentLibrary" && !d.createdBy?.user?.displayName?.startsWith("SharePoint")
      );

      const convertedToDriveItemObject = filteredDrives.map(d => ({
        name: i18n.global.te("DriveItem." + d.name) ? i18n.global.t("DriveItem." + d.name) : d.name,
        createdBy: d.createdBy,
        lastModifiedDateTime: d.lastModifiedDateTime,
        extension: FileHelper.getClosedFolderTemplate(),
        folder: true,
        parentReference: {
          driveId: d.id
        },
        webUrl: d.webUrl
      }));

      const rootDrive = filteredDrives.find(d => d.createdBy?.user?.displayName?.startsWith("System"));
      if (rootDrive) {
        sectionRootSystemDriveMap.value[sectionId] = rootDrive;
      }

      const otherDrives = convertedToDriveItemObject.filter(
        d => !d.createdBy?.user?.displayName?.startsWith("System")
      ) as unknown as RawDrive[];
      if (rootDrive) {
        sectionRootSubDrivesMap.value[rootDrive.id] = otherDrives;
      }
      // eslint-disable-next-line no-empty
    } catch {}
  };

  const loadAllSystemSectionDrives = () => {
    if (!mySectionsMap.value) {
      return;
    }
    for (const sectionId in mySectionsMap.value) {
      loadSystemDrivesForSection({ sectionId });
    }
  };

  const loadAllArchivedSystemSectionDrives = () => {
    const archived = archivedSectionsFlat.value;
    for (const sectionId in archived) {
      loadSystemDrivesForSection({ sectionId });
    }
  };

  return {
    // state
    mySectionsMap,
    myArchivedSectionGroupsMap,
    allSectionsOnInstitutionState,
    sectionRootDriveMap,
    sectionsTeachersMap,
    sectionsStudentsMap,
    sectionsProfilePictures,
    sectionRootSystemDriveMap,
    sectionRootSubDrivesMap,
    // sections actions
    loadMySections,
    reloadMySections,
    loadMyArchivedSections,
    reloadMyArchivedSections,
    loadAllSectionsOnInstitution,
    // members actions
    addSectionToMySections,
    removeSectionFromMySections,
    loadMembersForSection,
    sectionsLoadProfilePicturesFromList,
    sectionsLoadProfilePicture,
    sectionsDoLoadProfilePicture,
    loadSectionWithFallback,
    loadArchivedSectionById,
    // drive actions
    loadDrives,
    reloadDrives,
    findFilesTabOnGeneralTeamsChannel,
    // system drives actions
    loadSystemDrivesForSection,
    loadAllSystemSectionDrives,
    loadAllArchivedSystemSectionDrives,
    // sections getters
    mySections,
    myArchivedSections,
    groupingTitleForArchivedSection,
    section,
    allSectionsOnInstitution,
    peopleInMySections,
    mySectionsFromSearch,
    allStudentsAsPeopleTableObjectsFromSectionsSearched,
    allMyStudentsSearchedAsPeopleTableObejcts,
    sectionIdForPersonId,
    // members getters
    archivedSectionsFlat,
    sectionWithArchivedFallback,
    allMyTeachers,
    allMyStudents,
    sectionMember,
    sectionMembers,
    sectionTeachers,
    sectionStudents,
    // drive getters
    sharedFilesDrive,
    classMaterialsDrive,
    studentWorkDrive,
    assignmentDrive,
    sectionDriveType,
    // system drives getters
    allSectionsDrives,
    allArchivedSectionsDrives,
    sectionSubDrives,
    parentOfSubDrive,
    sectionFromDriveId
  };
});
